import { useId, useState } from 'react';
import { RxTimelineStatus, type TimelineEvent } from '../../api';
import Caret, { CARET_SIZE } from 'design-system/src/components/Caret/Caret';
import { Typography } from 'design-system/src/components/Typography/Typography';
import styled from 'styled-components';
import ChevronDown from 'design-system/src/assets/icons/chevronDown.svg?react';
import CheckingBoxInverted from 'design-system/src/assets/icons/checkingBoxInverted.svg?react';
import DrugInverted from 'design-system/src/assets/icons/drugInverted.svg?react';
import NewspaperFilled from 'design-system/src/assets/icons/newspaperFilled.svg?react';
import { titleCase } from '../../utils';
import { ExpandingContainer } from 'design-system/src/components/ExpandingContainer/ExpandingContainer';
import { TruncatedText } from 'design-system/src/components/TruncatedText/TruncatedText';
import { createTrackingProps } from 'shared-utils/analytics';
import TimelineEventItem from '../TimelineEventItem/TimelineEventItem';
import Checkmark from 'design-system/src/assets/icons/checkmark.svg?react';
import Cancel from 'design-system/src/assets/icons/cancel.svg?react';
import { focusVisibleStyles, standardTransitionStyles } from 'design-system/src/sharedStyles';
import type { StatusTimelineCategory } from '../rx/types';
import NotificationDot from 'design-system/src/components/NotificationDot/NotificationDot';

const CARET_PADDING = 6;

// TODO: move these into javascript/design-system/src/index.css as css variables
// TODO(tokens): Replace hex color value #f6f9fa with numbered equivalent once available.
const EXTRA_EXTRA_LIGHT_BLUE = '#f6f9fa';
// TODO(tokens): Replace hex color value #f9f5fd with numbered equivalent once available.
const EXTRA_EXTRA_LIGHT_PURPLE = '#f9f5fd';
// TODO(tokens): Replace hex color value #f6faf8 with numbered equivalent once available.
const EXTRA_EXTRA_LIGHT_GREEN = '#f6faf8';

const BORDER_RADIUS = 'var(--border-radius-medium)';

const StyledCollapsibleTimeline = styled.div`
  border-radius: ${BORDER_RADIUS};
  border: 1px solid var(--grayscale-100);
  background-color: var(--white);
`;

const Trigger = styled.button<{ $isOpen: boolean }>`
  all: unset;
  cursor: pointer;
  display: block;
  width: 100%;
  border-radius: ${({ $isOpen }) => ($isOpen ? `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0` : BORDER_RADIUS)};
  // required for z-index to work
  position: relative;
  // required for the focus outline to be visible over the open timeline
  z-index: 1;
  ${standardTransitionStyles}

  &:hover {
    background-color: var(--grayscale-50);
  }

  ${focusVisibleStyles}
`;

const EventHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px;
`;

const SpaceBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  box-sizing: border-box;
  gap: 16px;
`;

const IconAndStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryIconContainer = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  min-width: 24px;
  min-height: 24px;
  color: var(--white);
  background-color: ${({ $color }) => $color};
  // 1px left margin to align with the rail
  margin-left: 1px;
`;

const SubwayTrack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 24px;
  align-items: center;
  height: 48px;
`;

const Rail = styled.div<{ $hasBorder: boolean; $color: string; $height?: string }>`
  background-color: ${({ $hasBorder, $color }) => ($hasBorder ? $color : 'transparent')};
  height: ${({ $height }) => $height || '100%'};
  width: 1px;
`;

const StatusIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  min-height: 16px;
  color: var(--white);
  border-radius: 50%;
`;

const CheckmarkContainer = styled(StatusIconContainer)`
  background-color: var(--green-400);
`;

const CancelContainer = styled(StatusIconContainer)`
  background-color: var(--grayscale-300);
`;

const StyledCaret = styled(Caret)`
  padding: ${CARET_PADDING}px;
`;

const CaretPlaceholder = styled.div`
  min-width: ${CARET_SIZE + CARET_PADDING * 2}px;
  min-height: ${CARET_SIZE + CARET_PADDING * 2}px;
`;

const StyledExpandingContainer = styled(ExpandingContainer)<{ $color: string }>`
  background-color: ${({ $color }) => $color};
  padding: 0 16px;
  border-radius: 0 0 ${BORDER_RADIUS} ${BORDER_RADIUS};
`;

const getCategoryTitle = (category: StatusTimelineCategory) => {
  switch (category) {
    case 'pap':
      return `Patient Assistance (PAP) Program`;
    case 'bridge':
    case 'hub':
      return `${titleCase(category)} Program`;
    case 'coverage':
    case 'pharmacy':
    default:
      return titleCase(category);
  }
};

const getEventsBackgroundColor = (category: StatusTimelineCategory) => {
  if (category === 'coverage') {
    return EXTRA_EXTRA_LIGHT_BLUE;
  } else if (category === 'pharmacy') {
    return EXTRA_EXTRA_LIGHT_GREEN;
  } else {
    return EXTRA_EXTRA_LIGHT_PURPLE;
  }
};

const getIconColor = (category: StatusTimelineCategory) => {
  if (category === 'coverage') {
    return 'var(--teal-500)';
  } else if (category === 'pharmacy') {
    return 'var(--green-400)';
  } else {
    return 'var(--purple-700)';
  }
};

const CategoryIcon = ({ category }: { category: StatusTimelineCategory }) => {
  let icon = <NewspaperFilled width={17} height={16} />;

  if (category === 'coverage') {
    icon = <CheckingBoxInverted width={16} height={16} />;
  } else if (category === 'pharmacy') {
    icon = <DrugInverted width={16} height={16} />;
  }

  return <CategoryIconContainer $color={getIconColor(category)}>{icon}</CategoryIconContainer>;
};

type TimelineHeaderProps = {
  category: StatusTimelineCategory;
  status: RxTimelineStatus;
  statusDetail: string;
  isCompleteOrCanceled: boolean;
  showSubwayTrack?: boolean;
  isOpen?: boolean;
  caretElement?: React.ReactNode;
};

const renderStatus = (status: RxTimelineStatus, isCompleteOrCanceled: boolean, statusDetail: string) => {
  const statusText = (
    <Typography
      styledAs="bodyExtraSmallDMSans"
      renderedAs="div"
      weight={isCompleteOrCanceled ? 600 : 400}
      color={isCompleteOrCanceled ? 'var(--grayscale-900)' : 'var(--grayscale-600)'}
    >
      <TruncatedText lines={1}>{statusDetail}</TruncatedText>
    </Typography>
  );

  if (status === 'awaiting_office') {
    return <NotificationDot srOnlyText="Needs office input">{statusText}</NotificationDot>;
  }
  if (status === 'completed_success') {
    return (
      <>
        {statusText}
        <CheckmarkContainer>
          <Checkmark width={8} height={8} />
        </CheckmarkContainer>
      </>
    );
  }
  if (status === 'completed_non_success') {
    return (
      <>
        {statusText}
        <CancelContainer>
          <Cancel width={8} height={8} />
        </CancelContainer>
      </>
    );
  }
  return statusText;
};

/**
 * Reusable header component for timeline items that displays category icon, title, status, and optional subway track
 */
const TimelineHeader = ({
  category,
  status,
  statusDetail,
  isCompleteOrCanceled,
  showSubwayTrack = false,
  isOpen = false,
  caretElement,
}: TimelineHeaderProps) => {
  return (
    <EventHeader>
      <SpaceBetween>
        <IconAndStatus>
          {showSubwayTrack ? (
            <SubwayTrack>
              <Rail $hasBorder={false} $color={getIconColor(category)} />
              <CategoryIcon category={category} />
              <Rail $hasBorder={isOpen} $color={getIconColor(category)} />
            </SubwayTrack>
          ) : (
            <CategoryIcon category={category} />
          )}
          <Typography styledAs="bodySmallDMSans" weight={600}>
            <TruncatedText lines={1}>{getCategoryTitle(category)}</TruncatedText>
          </Typography>
        </IconAndStatus>

        <IconAndStatus>
          {renderStatus(status, isCompleteOrCanceled, statusDetail)}
          {caretElement || <CaretPlaceholder />}
        </IconAndStatus>
      </SpaceBetween>
    </EventHeader>
  );
};

export type CollapsibleTimelineProps = {
  analyticsName: string;
  category: StatusTimelineCategory;
  status: RxTimelineStatus;
  statusDetail?: string;
  events: TimelineEvent[];
};

/**
 * Timeline of events where the status is always visible and the past events (if they exist)
 * can be toggled between collapsed and expanded.
 */
export const CollapsibleTimeline = ({
  analyticsName,
  category,
  status,
  statusDetail = 'Not yet started',
  events,
}: CollapsibleTimelineProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const collapsibleBodyId = useId();
  const isCompleteOrCanceled = status === 'completed_success' || status === 'completed_non_success';

  if (!events || events.length === 0) {
    return (
      <StyledCollapsibleTimeline>
        <div {...createTrackingProps({ elementName: `${analyticsName} Status`, unmask: true })}>
          <TimelineHeader
            category={category}
            status={status}
            statusDetail={statusDetail}
            isCompleteOrCanceled={isCompleteOrCanceled}
            showSubwayTrack={false}
          />
        </div>
      </StyledCollapsibleTimeline>
    );
  }

  return (
    <StyledCollapsibleTimeline>
      <Trigger
        $isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        {...createTrackingProps({ elementName: `${analyticsName} Toggle` })}
      >
        <TimelineHeader
          category={category}
          status={status}
          statusDetail={statusDetail}
          isCompleteOrCanceled={isCompleteOrCanceled}
          showSubwayTrack={true}
          isOpen={isOpen}
          caretElement={
            <StyledCaret
              isOpen={isOpen}
              // this CollapsibleTimeline component doesn't animate open/closed, so we don't want the caret to animate either
              animationDuration={0}
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
              aria-expanded={isOpen}
              aria-controls={collapsibleBodyId}
              // Caret is a button html element, so we need to change it to a div to avoid having a button inside a button
              forwardedAs="div"
              {...createTrackingProps({ elementName: `${analyticsName} Toggle` })}
            >
              <ChevronDown title={isOpen ? 'Collapse timeline' : 'Expand timeline'} />
            </StyledCaret>
          }
        />
      </Trigger>

      <StyledExpandingContainer
        id={collapsibleBodyId}
        visible={isOpen}
        animationDuration={0}
        $color={getEventsBackgroundColor(category)}
      >
        {events.map((event, index) => (
          <TimelineEventItem
            key={`${event.event?.toString()}-${event.timestamp}`}
            event={event}
            isLastEvent={index === events.length - 1}
            subwayTrackColor={getIconColor(category)}
          />
        ))}
      </StyledExpandingContainer>
    </StyledCollapsibleTimeline>
  );
};
